import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, coerceBooleanFromString, tzHeadersSchema, projectIdParamsSchema } from '../schemas';
import { phoneNumberField } from '../../schemas/common.schemas';
import { managerSchema } from '../../interfaces/IManager';
import { baseManagerSchema } from '../../interfaces/IBaseManager';
import { tradeSchema } from '../../interfaces/ITrade';

// ============================================================
// Param Schemas
// ============================================================

const projectManagerParamsSchema = z.object({
	projectId: requiredStringSchema,
	managerId: requiredStringSchema,
});

// ============================================================
// Query Schemas
// ============================================================

const managersQuerySchema = z.object({
	startDate: z.string().optional().describe('Start date filter'),
	endDate: z.string().optional().describe('End date filter'),
	includeDeleted: coerceBooleanFromString.optional().describe('Include soft-deleted managers'),
});

// ============================================================
// Body Schemas
// ============================================================

/** `projectId` is addressed by the URI, so it must not also travel in the body. */
const createManagerBodySchema = baseManagerSchema.omit({ projectId: true });

const updateManagerBodySchema = z.object({
	phoneNumber: phoneNumberField,
	receiveNotifications: z.boolean().optional(),
});

// ============================================================
// Contract
// ============================================================

/**
 * Managers are project data: `/projects/:projectId/managers`.
 *
 * `{ root: true }` — two routes here are NOT project data and therefore spell
 * their old segments literally: `getManagersByOrganization` (organization scope)
 * and `updateManagersTrade` (global admin). Without that they would silently
 * start serving `/api/projectConfig/organization/:organizationId` and
 * `/api/projectConfig/updateTrade`.
 *
 * `getManagersByProject` HAD to move regardless of scope: its path was a bare
 * `/:projectId`, which under `root` becomes `/api/projectConfig/:projectId` — a
 * catch-all shadowing every sibling route in the service.
 */
export const managerContract = defineContract(
	'managers',
	{
		/** Organization scope — an org's manager roster spans its projects. */
		getManagersByOrganization: route({
			method: 'GET',
			path: '/managers/organization/:organizationId',
			params: z.object({
				organizationId: requiredStringSchema,
			}),
			query: managersQuerySchema,
			response: z.array(managerSchema),
			operationId: 'getManagersByOrganization',
			tags: ['Managers'],
			summary: 'List the managers of an organization',
		}),
		/**
		 * Global admin. Entities calls this on a trade rename and every manager
		 * carrying that trade must follow, across every project — there is no
		 * single project to scope it by. Same shape, and the same justification, as
		 * `activities/activity.contract.ts::updateTrade`.
		 */
		updateManagersTrade: route({
			method: 'PUT',
			path: '/managers/updateTrade',
			body: tradeSchema,
			response: z.string(),
			operationId: 'updateManagersTrade',
			tags: ['Managers'],
			summary: 'Propagate a renamed trade onto every manager carrying it',
		}),
		getManagersByProject: route({
			method: 'GET',
			path: '/projects/:projectId/managers',
			params: projectIdParamsSchema,
			query: managersQuerySchema,
			response: z.array(managerSchema),
			operationId: 'getManagersByProject',
			tags: ['Managers'],
			summary: 'List the managers of a project',
		}),
		createManager: route({
			method: 'POST',
			path: '/projects/:projectId/managers',
			params: projectIdParamsSchema,
			headers: tzHeadersSchema,
			body: createManagerBodySchema,
			response: managerSchema,
			operationId: 'createManager',
			tags: ['Managers'],
			summary: 'Create a manager in a project',
		}),
		updateManager: route({
			method: 'PUT',
			path: '/projects/:projectId/managers/:managerId',
			params: projectManagerParamsSchema,
			headers: tzHeadersSchema,
			body: updateManagerBodySchema,
			response: managerSchema,
			operationId: 'updateManager',
			tags: ['Managers'],
			summary: 'Update a manager of a project',
		}),
		deleteManager: route({
			method: 'DELETE',
			path: '/projects/:projectId/managers/:managerId',
			params: projectManagerParamsSchema,
			response: z.string(),
			operationId: 'deleteManager',
			tags: ['Managers'],
			summary: 'Soft-delete a manager of a project',
		}),
	},
	{ root: true }
);
